import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Target, Clock, Smartphone, MapPin, ArrowRight, Sparkles, CheckCircle, Lock } from 'lucide-react'
import clsx from 'clsx'
import { apiUrl } from '../config/api'
import { PLATFORM_CONFIG } from '../data/mock'
import PullToRefresh from '../components/ui/PullToRefresh'

interface Mission {
  id: number
  title: string
  description: string | null
  action_type: string
  target_platforms: string | null
  points: number
  points_per_platform: number
  bonus_points: number
  deadline: string | null
  status: string
  category_name: string | null
  category_color: string | null
  submissions_count: number
  my_submissions_count: number
  created_at: string
}

type StatusTab = 'active' | 'all' | 'completed' | 'expired'

const digitalTypes = ['post_social', 'share_whatsapp']

const actionLabels: Record<string, { label: string; instruction: string }> = {
  post_social: { label: 'Postar nas redes', instruction: 'Salve o material, copie o texto e publique nas redes sociais' },
  share_whatsapp: { label: 'WhatsApp', instruction: 'Compartilhe no WhatsApp' },
  repost: { label: 'Repostar', instruction: 'Abra o post e reposte/compartilhe' },
  comment: { label: 'Comentar', instruction: 'Abra o post e deixe seu comentário' },
  engage: { label: 'Engajar', instruction: 'Reposte e comente no post' },
  take_photo: { label: 'Tirar foto', instruction: 'Tire uma foto como comprovante' },
  checkin: { label: 'Check-in', instruction: 'Vá ao local e faça check-in' },
  sticker_car: { label: 'Adesivo no carro', instruction: 'Cole o adesivo no carro e tire uma foto' },
  sticker_house: { label: 'Adesivo na fachada', instruction: 'Cole o adesivo na fachada e tire uma foto' },
  door_to_door: { label: 'Porta a porta', instruction: 'Faça visitas e registre' },
  attend_event: { label: 'Evento', instruction: 'Participe do evento e tire uma foto' },
}

function isNew(createdAt: string) {
  const created = new Date(createdAt)
  const now = new Date()
  return (now.getTime() - created.getTime()) < 48 * 60 * 60 * 1000
}

export default function MissionsPage() {
  const [missions, setMissions] = useState<Mission[]>([])
  const [loading, setLoading] = useState(true)
  const [statusTab, setStatusTab] = useState<StatusTab>('active')
  const { slug } = useParams()
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchMissions = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true)
    else setLoadingMore(true)
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(apiUrl(`/v1/community/missions?tab=${statusTab}&page=${pageNum}&per_page=10`), {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      const data = await res.json()
      const fresh: Mission[] = data.data || []
      const meta = data.meta || {}

      if (append) {
        setMissions(prev => [...prev, ...fresh])
      } else {
        setMissions(fresh)
      }
      setHasMore(meta.page < meta.last_page)
    } catch {} finally { setLoading(false); setLoadingMore(false) }
  }, [statusTab])

  useEffect(() => { setPage(1); fetchMissions(1) }, [fetchMissions])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') { setPage(1); fetchMissions(1) }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchMissions])

  const handleRefresh = async () => { setPage(1); await fetchMissions(1) }

  function loadMore() {
    const next = page + 1
    setPage(next)
    fetchMissions(next, true)
  }

  const formatDeadline = (d: string | null) => {
    if (!d) return null
    const deadline = new Date(d)
    const now = new Date()
    const hoursLeft = Math.max(0, Math.floor((deadline.getTime() - now.getTime()) / (1000 * 60 * 60)))
    if (hoursLeft <= 0) return 'Encerrada'
    if (hoursLeft < 24) return `${hoursLeft}h restantes`
    const days = Math.floor(hoursLeft / 24)
    return `${days} dia${days > 1 ? 's' : ''}`
  }

  const tabs: { key: StatusTab; label: string }[] = [
    { key: 'active', label: 'Ativas' },
    { key: 'completed', label: 'Concluídas' },
    { key: 'expired', label: 'Expiradas' },
    { key: 'all', label: 'Todas' },
  ]

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="h-12 px-4 items-center border-b border-border-subtle hidden lg:flex">
        <Target size={20} className="text-content-muted mr-2" />
        <h1 className="text-base font-semibold text-content-normal">Missões</h1>
      </div>

      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto w-full">
        {/* Filtros */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusTab(tab.key)}
              className={clsx(
                'px-4 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap',
                statusTab === tab.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-surface-secondary text-content-muted hover:bg-surface-hover',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl border border-border-subtle p-5 animate-pulse">
                <div className="h-5 w-2/3 bg-gray-200 rounded mb-3" />
                <div className="h-3 w-full bg-gray-100 rounded mb-2" />
                <div className="h-3 w-1/2 bg-gray-100 rounded mb-4" />
                <div className="h-10 w-full bg-gray-100 rounded-xl" />
              </div>
            ))}
          </div>
        ) : missions.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-surface-secondary flex items-center justify-center mx-auto mb-4">
              <Target size={32} className="text-content-muted opacity-40" />
            </div>
            <p className="text-sm font-medium text-content-muted">
              {statusTab === 'active' ? 'Nenhuma missão ativa no momento' : 'Nenhuma missão nesta categoria'}
            </p>
            <p className="text-xs text-content-muted mt-1">Puxe pra baixo pra atualizar</p>
          </div>
        ) : (
          <>
          <div className="space-y-3">
            {missions.map((mission) => {
              const isDigital = digitalTypes.includes(mission.action_type)
              const CatIcon = isDigital ? Smartphone : MapPin
              const deadlineText = formatDeadline(mission.deadline)
              const isRecent = isNew(mission.created_at)
              const myDone = mission.my_submissions_count > 0
              const isCompleted = myDone
              const deadlinePassed = mission.deadline && new Date(mission.deadline) < new Date()
              const isExpired = deadlinePassed && !myDone
              const actionInfo = actionLabels[mission.action_type] || { label: mission.action_type, instruction: '' }

              return (
                <button
                  key={mission.id}
                  onClick={() => navigate(`/${slug}/missoes/${mission.id}`)}
                  className={clsx(
                    'w-full text-left rounded-2xl border transition-all active:scale-[0.98] overflow-hidden',
                    isCompleted
                      ? 'border-status-online/20 bg-status-online/[0.03]'
                      : isExpired
                        ? 'border-border-subtle opacity-50'
                        : 'border-border-subtle bg-surface-secondary shadow-sm',
                  )}
                >
                  <div className="p-4">
                    {/* Tipo + prazo */}
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] text-content-muted font-medium">
                        <CatIcon size={10} />
                        {actionInfo.label}
                      </span>
                      <div className="flex items-center gap-2">
                        {deadlineText && !isExpired && (
                          <span className="flex items-center gap-1 text-[10px] text-content-muted">
                            <Clock size={10} /> {deadlineText}
                          </span>
                        )}
                        {isRecent && !isCompleted && !isExpired && (
                          <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[9px] font-bold">
                            Nova
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Título */}
                    <h3 className="text-sm font-bold text-content-normal mb-2">{mission.title}</h3>

                    {/* Rodapé */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-content-muted">+{mission.points} pts</span>
                      {isCompleted && (
                        <span className="flex items-center gap-1 text-xs font-bold text-status-online">
                          <CheckCircle size={12} /> Concluída
                        </span>
                      )}
                      {isExpired && (
                        <span className="flex items-center gap-1 text-xs text-content-muted">
                          <Lock size={12} /> Encerrada
                        </span>
                      )}
                      {!isCompleted && !isExpired && (
                        <span className="text-xs font-medium text-primary">Participar</span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Ver mais */}
          {hasMore && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full py-3 text-sm font-medium text-primary hover:bg-surface-hover rounded-xl transition-colors"
            >
              {loadingMore ? 'Carregando...' : 'Ver mais'}
            </button>
          )}
        </>
        )}
      </div>
    </PullToRefresh>
  )
}
